import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { multipartUploadViaWS, type UploadStartedInfo } from "../uploader";
import { useFocusTrap } from "../utils/focusTrap";
import { useStore } from "../store";
import { getWs } from "../wsConnection";
import type { Application } from "@interlucent/organization-protocol";
import type { AppVersionMetadata } from "../types/appVersionMetadata";
import { BuildArchiveSection } from "./BuildArchiveSection";
import { ZipTreeDropdown } from "./ZipTreeDropdown";
import type { ZipAnalysis } from "../utils/zipAnalyzer";
import { CommandArgsBuilder } from "./CommandArgsBuilder";
import { EnvVarsEditor } from "./EnvVarsEditor";
import { isArgDisallowed, isEnvVarDisallowed } from "./versionFieldDisallowList";
import { usePlatformAdmin } from "../hooks/usePlatformAdmin";
import { logger } from '../log';
import styles from './AddVersionDialog.module.css';

const log = logger('add-version');

interface AddVersionDialogProps {
  application: Application;
  onClose: () => void;
  onUploadStarted: (info: UploadStartedInfo) => void;
  replaceVersionId?: string;
}

export function AddVersionDialog({
  application,
  onClose,
  onUploadStarted,
  replaceVersionId
}: AddVersionDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { isPlatformAdmin } = usePlatformAdmin();
  const [metadata, setMetadata] = useState<AppVersionMetadata>({
    gameEngine: "unreal",
    operatingSystem: "windows",
    executablePath: "",
    // Manual PS protocol selection; default PS1 (legacy). Never auto-inferred.
    streamingProtocolVersion: 1
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [zipAnalysis, setZipAnalysis] = useState<ZipAnalysis | null>(null);
  const [showExecDropdown, setShowExecDropdown] = useState(false);
  const execFieldRef = useRef<HTMLDivElement>(null);
  const [fieldsTouched, setFieldsTouched] = useState({
    gameEngine: false,
    operatingSystem: false,
    executablePath: false
  });

  const engineOS = useMemo(() => ({
    gameEngine: metadata.gameEngine,
    operatingSystem: metadata.operatingSystem,
  }), [metadata.gameEngine, metadata.operatingSystem]);

  const checkArgDisallowed = useCallback(
    (arg: string) => isArgDisallowed(arg, engineOS),
    [engineOS],
  );

  const checkEnvDisallowed = useCallback(
    (key: string) => isEnvVarDisallowed(key, engineOS),
    [engineOS],
  );

  // Enable focus trap
  useFocusTrap(modalRef, true);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleMetadataChange = useCallback((patch: Partial<AppVersionMetadata>) => {
    setMetadata(prev => ({ ...prev, ...patch }));
  }, []);

  const handleArchiveChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    setMetadata(prev => ({ ...prev, executablePath: "" }));
    setFieldsTouched(prev => ({ ...prev, executablePath: false }));
    setZipAnalysis(null);
  }, []);

  const markFieldTouched = useCallback((field: keyof typeof fieldsTouched) => {
    setFieldsTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  useEffect(() => {
    if (!zipAnalysis) {
      setShowExecDropdown(false);
    }
  }, [zipAnalysis]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!execFieldRef.current) return;
      if (!execFieldRef.current.contains(event.target as Node)) {
        setShowExecDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleExecutablePick = useCallback((path: string) => {
    markFieldTouched("executablePath");
    setMetadata(prev => ({ ...prev, executablePath: path }));
    setShowExecDropdown(false);
  }, [markFieldTouched]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!metadata.executablePath.trim()) {
      alert("Executable path is required");
      return;
    }
    if (!selectedFile) {
      alert("Select a build archive before continuing");
      return;
    }
    onClose();

    if (!getWs()) {
      log.error('WebSocket not available for upload');
      return;
    }

    let currentUploadId: string | undefined;
    const checkPaused = () => {
      if (!currentUploadId) return false;
      const currentState = useStore.getState();
      return currentState.uploadProgress[currentUploadId]?.paused === true;
    };

    try {
      await multipartUploadViaWS(
        selectedFile,
        () => {}, // Progress callback not needed - tracked elsewhere
        application.projectId,
        (info) => {
          currentUploadId = info.uploadId;
          onUploadStarted(info);
        },
        undefined, // resumeUploadId
        {
          ...metadata,
          executablePath: metadata.executablePath.trim()
        },
        undefined, // appName (not needed - using existing app)
        application.id, // appId
        replaceVersionId, // replaceVersionId
        checkPaused // Check if paused
      );
    } catch (e: any) {
      if (e.message === 'upload_paused') {
        log.info('upload paused by user');
      } else {
        log.error('upload error', e);
      }
    }
  };

  const dialogId = replaceVersionId ? 'replace-version' : 'add-version';

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${dialogId}-title`}
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id={`${dialogId}-title`} className={styles.modalTitle}>
          {replaceVersionId ? 'Replace Version' : 'Add Version'}
        </h2>
        <p className={styles.modalSubtitle}>
          {replaceVersionId ? 'for' : 'to'} <strong>{application.name}</strong>
        </p>

        <form onSubmit={handleFormSubmit}>
            <BuildArchiveSection
              dialogId={dialogId}
              selectedFile={selectedFile}
              onFileChange={handleArchiveChange}
              metadata={metadata}
              onMetadataChange={handleMetadataChange}
              canAutofill={{
                gameEngine: !fieldsTouched.gameEngine,
                operatingSystem: !fieldsTouched.operatingSystem,
                executablePath: !fieldsTouched.executablePath
              }}
              onAnalysisChange={setZipAnalysis}
            />

            <div className={styles.formGroup}>
              <label htmlFor={`${dialogId}-game-engine`} className={styles.label}>
                Game Engine *
              </label>
              <select
                id={`${dialogId}-game-engine`}
                value={metadata.gameEngine}
                onChange={(e) => {
                  markFieldTouched("gameEngine");
                  setMetadata(prev => ({ ...prev, gameEngine: e.target.value as "unreal" | "unity" }));
                }}
                className={styles.select}
                required
              >
                <option value="unreal">Unreal Engine</option>
                <option value="unity">Unity</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor={`${dialogId}-os`} className={styles.label}>
                Operating System *
              </label>
              <select
                id={`${dialogId}-os`}
                value={metadata.operatingSystem}
                onChange={(e) => {
                  markFieldTouched("operatingSystem");
                  setMetadata(prev => ({ ...prev, operatingSystem: e.target.value as "windows" | "linux" }));
                }}
                className={styles.select}
                required
              >
                <option value="windows">Windows</option>
              </select>
            </div>

            <div className={`${styles.formGroup} ${styles.execFieldGroup}`} ref={execFieldRef}>
              <label htmlFor={`${dialogId}-exec-path`} className={styles.label}>
                Executable Path *
              </label>
              <div className={styles.execInputWrapper}>
                <input
                  id={`${dialogId}-exec-path`}
                  type="text"
                  value={metadata.executablePath}
                  onFocus={() => zipAnalysis && setShowExecDropdown(true)}
                  onChange={(e) => {
                    markFieldTouched("executablePath");
                    setMetadata(prev => ({ ...prev, executablePath: e.target.value }));
                  }}
                  placeholder="e.g., GameServer.exe or bin/server"
                  autoFocus
                  className={styles.input}
                  required
                />
                <ZipTreeDropdown
                  analysis={zipAnalysis}
                  isOpen={Boolean(showExecDropdown && zipAnalysis && selectedFile)}
                  selectedPath={metadata.executablePath}
                  onSelect={handleExecutablePick}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor={`${dialogId}-cmd-args`} className={styles.label}>
                Command Arguments (optional)
              </label>
              <CommandArgsBuilder
                id={`${dialogId}-cmd-args`}
                value={metadata.cmdArgs || ''}
                onChange={(value) => setMetadata(prev => ({ ...prev, cmdArgs: value }))}
                checkDisallowed={checkArgDisallowed}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor={`${dialogId}-env-vars`} className={styles.label}>
                Environment Variables (optional)
              </label>
              <EnvVarsEditor
                id={`${dialogId}-env-vars`}
                value={metadata.environmentVariables || ''}
                onChange={(value) => setMetadata(prev => ({ ...prev, environmentVariables: value }))}
                checkDisallowed={checkEnvDisallowed}
              />
            </div>

            {isPlatformAdmin && metadata.gameEngine === 'unreal' && (
              <div className={styles.formGroup}>
                <label htmlFor={`${dialogId}-streaming-protocol-version`} className={styles.label}>
                  Pixel Streaming Version
                </label>
                <select
                  id={`${dialogId}-streaming-protocol-version`}
                  value={metadata.streamingProtocolVersion === 2 ? '2' : '1'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMetadata(prev => ({
                      ...prev,
                      streamingProtocolVersion: v === '2' ? 2 : 1,
                    }));
                  }}
                  className={styles.select}
                >
                  <option value="1">Pixel Streaming 1.x (UE 4.27 – 5.4)</option>
                  <option value="2">Pixel Streaming 2 (UE 5.5+)</option>
                </select>
                <span className={styles.hint}>
                  Set manually per build — not inferred from engine version. Select PS2
                  only for builds packaged with the Pixel Streaming 2 plugin.
                </span>
              </div>
            )}

            {isPlatformAdmin && (
              <div className={styles.formGroup}>
                <label htmlFor={`${dialogId}-worker-game-rendezvous`} className={styles.label}>
                  Worker / Game Rendezvous (seconds, optional)
                </label>
                <input
                  id={`${dialogId}-worker-game-rendezvous`}
                  type="number"
                  min={1}
                  max={600}
                  step={1}
                  value={
                    typeof metadata.workerGameRendezvousSeconds === 'number'
                      ? String(metadata.workerGameRendezvousSeconds)
                      : ''
                  }
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    const parsed = v === '' ? undefined : Number(v);
                    const next =
                      parsed !== undefined &&
                      Number.isFinite(parsed) &&
                      parsed >= 1 &&
                      parsed <= 600
                        ? Math.trunc(parsed)
                        : undefined;
                    setMetadata(prev => ({ ...prev, workerGameRendezvousSeconds: next }));
                  }}
                  placeholder="Default (90s)"
                  className={styles.input}
                />
              </div>
            )}

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.button}
                disabled={!selectedFile}
              >
                Upload Build
              </button>
            </div>
        </form>
      </div>
    </div>
  );
}
